import React, { useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import WifiIcon from "@mui/icons-material/Wifi";
import logoSmall from "../assets/logo_small.png";
import { SERVER_URL } from "../api";
import LanguageSelector from "./LanguageSelector";
import WiFiConfigModal from "./WiFiConfigModal";
import { useTranslation } from "react-i18next";

/**
 * Componente reutilizable para mostrar los logos del evento y ubicación
 * @param {Object} props
 * @param {Object} props.eventData - Datos del evento (con logo, cliente (objeto poblado con logo), marcaBlanca, ubicacion)
 * @param {Object} props.businessData - Datos del business (con logoURL, direccion, etc.)
 * @param {boolean} props.showUbicacion - Mostrar barra de ubicación (default: true)
 * @param {boolean} props.showLanguageSelector - Mostrar selector de idioma (default: true)
 */
const EventHeader = ({ eventData, businessData, showUbicacion = true, showLanguageSelector = true, showWifi = true }) => {
  const { t } = useTranslation();
  const [wifiModalOpen, setWifiModalOpen] = useState(false);
  
  // Obtener datos WiFi del espacio
  const wifiData = eventData?.espacio?.wifi;
  // Obtener ubicación: primero del evento, si no tiene usar la del espacio o del business
  const ubicacionEvento = eventData?.ubicacion;
  
  // Construir ubicación del espacio si existe y tiene datos
  let ubicacionEspacio = null;
  if (eventData?.espacio?.direccion) {
    const espacioDir = eventData.espacio.direccion;
    // Verificar si el espacio tiene al menos uno de los campos de dirección
    const espacioTieneDireccion = espacioDir.direccion || espacioDir.codigoPostal || espacioDir.poblacion ||
      (espacioDir.poblacion && typeof espacioDir.poblacion === 'object');
    
    if (espacioTieneDireccion) {
      ubicacionEspacio = {
        direccion: espacioDir.direccion,
        codigoPostal: espacioDir.codigoPostal,
        poblacion: espacioDir.poblacion, // Mantener como objeto para que getTextValue lo procese
        provincia: espacioDir.provincia,
        pais: espacioDir.pais,
        coordenadas: espacioDir.coordenadas,
      };
    }
  }
  
  const ubicacionBusiness = businessData ? {
    direccion: businessData.direccion,
    codigoPostal: businessData.codigoPostal || businessData.codigo_postal,
    poblacion: businessData.localidad?.nombre || businessData.localidad?.fullName || businessData.poblacion,
    coordenadas: businessData.coordenadas || null,
  } : null;
  
  // Prioridad: ubicacion del evento > ubicacion del espacio > ubicacion del business
  // Verificar si el evento tiene datos útiles de ubicación (dirección o código postal, no solo población)
  const tieneUbicacionEventoCompleta = ubicacionEvento && (
    (ubicacionEvento.direccion && ubicacionEvento.direccion.trim()) || 
    (ubicacionEvento.codigoPostal && ubicacionEvento.codigoPostal.trim())
  );
  
  // Si el evento tiene ubicación completa, usarla. Si solo tiene población, dar prioridad al espacio si está disponible
  const ubicacion = tieneUbicacionEventoCompleta
    ? ubicacionEvento
    : (ubicacionEspacio || ubicacionEvento || ubicacionBusiness);

  // Función para obtener el texto de un campo (maneja ObjectId, objetos y strings)
  const getTextValue = (value) => {
    if (!value) return null;
    // Si es un objeto con nombre o fullName, usar ese valor
    if (typeof value === 'object' && value.nombre) return value.nombre;
    if (typeof value === 'object' && value.fullName) return value.fullName;
    // Si es un objeto con _id (ObjectId), no mostrar
    if (typeof value === 'object' && value._id && !value.nombre && !value.fullName) return null;
    // Si es string, verificar si es un ObjectId (24 caracteres hexadecimales)
    if (typeof value === 'string') {
      // Si parece un ObjectId de MongoDB (24 caracteres hex), no mostrar
      if (/^[0-9a-fA-F]{24}$/.test(value)) return null;
      return value;
    }
    return null;
  };

  // Construir texto de ubicación - siempre incluir dirección, código postal y población si existen
  const getUbicacionTexto = () => {
    if (!ubicacion) return null;
    const partes = [];
    const direccion = getTextValue(ubicacion.direccion);
    const codigoPostal = getTextValue(ubicacion.codigoPostal);
    const poblacion = getTextValue(ubicacion.poblacion);
    
    // Orden: dirección, código postal, población
    if (direccion && direccion.trim()) partes.push(direccion.trim());
    if (codigoPostal && codigoPostal.trim()) partes.push(codigoPostal.trim());
    if (poblacion && poblacion.trim()) partes.push(poblacion.trim());
    
    return partes.length > 0 ? partes.join(", ") : null;
  };

  const ubicacionTexto = getUbicacionTexto();
  // Renderizar logos
  const renderLogos = () => {
    // Si marca blanca no está activada, mostrar solo Tipify
    if (!eventData?.marcaBlanca) {
      return (
        <div className="d-flex justify-content-center mb-4">
          <img
            src={logoSmall}
            alt="Tipify"
            className="logo"
            style={{ maxHeight: "80px", objectFit: "contain" }}
          />
        </div>
      );
    }

    // Determinar qué logos mostrar
    const getLogoUrl = (logo) => {
      if (!logo) return null;
      return logo.startsWith("http") ? logo : `${SERVER_URL}/${logo}`;
    };

    // El logo del negocio sale siempre que lo tenga, sea cual sea su plan
    const logoOrganizador = getLogoUrl(eventData?.logo) || getLogoUrl(businessData?.logoURL);
    // El logo del cliente ahora viene del objeto cliente poblado
    const logoCliente = getLogoUrl(eventData?.cliente?.logo);

    // Si no hay logos personalizados, mostrar Tipify
    if (!logoOrganizador && !logoCliente) {
      return (
        <div className="d-flex justify-content-center mb-4">
          <img
            src={logoSmall}
            alt="Tipify"
            className="logo"
            style={{ maxHeight: "80px", objectFit: "contain" }}
          />
        </div>
      );
    }

    // Si hay ambos logos, mostrarlos lado a lado
    if (logoOrganizador && logoCliente) {
      return (
        <div className="d-flex justify-content-between align-items-center mb-2" style={{ gap: "20px" }}>
          <img
            src={logoOrganizador}
            alt="Logo organizador"
            className="logo"
            style={{ maxHeight: "80px", maxWidth: "200px", objectFit: "contain", width: "inherit" }}
          />
          <img
            src={logoCliente}
            alt="Logo cliente"
            className="logo"
            style={{ maxHeight: "80px", maxWidth: "200px", objectFit: "contain", width: "inherit" }}
          />
        </div>
      );
    }

    // Si solo hay un logo, centrarlo
    const logoUnico = logoOrganizador || logoCliente;
    return (
      <div className="d-flex justify-content-center mb-2">
        <img
          src={logoUnico}
          alt={logoOrganizador ? "Logo organizador" : "Logo cliente"}
          className="logo"
          style={{ maxHeight: "80px", maxWidth: "200px", objectFit: "contain" }}
        />
      </div>
    );
  };

  return (
    <Box style={{ marginBottom: "24px", width: "100%" }}>
      {renderLogos()}
      
      {ubicacionTexto && showUbicacion && (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            marginTop: "16px",
            padding: "12px 16px",
            backgroundColor: "rgba(102, 188, 95, 0.15)",
            borderRadius: "8px",
          }}
        >
          <FmdGoodOutlinedIcon style={{ color: "#666", fontSize: "20px" }} />
          <Typography
            variant="body2"
            style={{
              color: "#666",
              fontSize: "0.875rem",
              textAlign: "center",
            }}
          >
            {ubicacionTexto}
          </Typography>
        </Box>
      )}
      
      {/* Botón WiFi y Selector de idioma en la misma línea */}
      {(wifiData?.ssid && showWifi || showLanguageSelector) && (
        <Box
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "12px",
            gap: "12px",
          }}
        >
          {/* Botón WiFi a la izquierda */}
          {wifiData?.ssid && showWifi && (
            <Button
              variant="outlined"
              startIcon={<WifiIcon />}
              onClick={() => setWifiModalOpen(true)}
              sx={{
                textTransform: "none",
                fontSize: "0.875rem",
                padding: "8px 16px",
                borderRadius: "20px",
                borderColor: "#66bc5f",
                color: "#66bc5f",
                "&:hover": {
                  borderColor: "#66bc5f",
                  backgroundColor: "rgba(102, 188, 95, 0.05)",
                },
                flex: { xs: "0 0 auto", sm: "0 0 auto" },
                minWidth: { xs: "auto", sm: "150px" },
                width: { xs: "fit-content", sm: "auto" },
                maxWidth: { xs: "220px", sm: "none" },
              }}
            >
              {t("wifi.connect")}
            </Button>
          )}
          
          {/* Selector de idioma a la derecha */}
          {showLanguageSelector && (
            <Box sx={{ ml: "auto" }}>
              <LanguageSelector />
            </Box>
          )}
        </Box>
      )}

      {/* Modal WiFi */}
      {wifiData?.ssid && (
        <WiFiConfigModal
          open={wifiModalOpen}
          onClose={() => setWifiModalOpen(false)}
          spaceData={eventData?.espacio}
        />
      )}
    </Box>
  );

};

export default EventHeader;

